import React, { useState, useEffect } from 'react';
import { LogOut, Package, Folder, Settings as SettingsIcon, ExternalLink, RefreshCcw } from 'lucide-react';
import Logo from '../common/Logo';
import ProductsTab from './Productstab';
import CategoriesTab from './Categoriestab';
import SettingsTab from './Settingstab';

const TABS = [
  { id: 'products', label: 'Products', icon: Package },
  { id: 'categories', label: 'Categories', icon: Folder },
  { id: 'settings', label: 'Settings', icon: SettingsIcon }
];

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [loading, setLoading] = useState(true);
  const [firebaseReady, setFirebaseReady] = useState(false);

  // Check Firebase connection on mount
  useEffect(() => {
    const checkFirebase = async () => {
      try {
        const { getCategories } = await import('../../data/storeData');

        // Test connection
        await getCategories();
        setFirebaseReady(true);
      } catch (error) {
        console.error('Firebase error:', error);
        alert('Firebase is not properly configured. Check console for details.');
        setFirebaseReady(false);
      } finally {
        setLoading(false);
      }
    };

    checkFirebase();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_login_time');
    window.location.href = '/admin';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <div className="absolute top-1/3 -right-32 w-[400px] h-[400px] bg-orange-500/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="text-center relative z-10">
          <div className="w-14 h-14 border-[3px] border-orange-500/20 border-t-orange-500 rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-white text-lg font-semibold">Loading Admin Panel</p>
          <p className="text-gray-500 text-sm mt-2">Connecting to Firebase...</p>
        </div>
      </div>
    );
  }

  if (!firebaseReady) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="bg-white/[0.06] backdrop-blur-2xl border border-red-500/30 rounded-3xl p-8 max-w-lg shadow-[0_24px_60px_-12px_rgba(0,0,0,0.8)]">
          <h2 className="text-2xl font-bold text-red-400 mb-4 tracking-tight">Firebase Configuration Error</h2>
          <p className="text-white mb-4">
            Cannot connect to Firebase. Please check:
          </p>
          <ul className="text-gray-400 space-y-2 mb-8 text-sm">
            <li>• Firebase config file exists at <code className="bg-white/10 px-2 py-1 rounded-md text-gray-200">src/firebase/config.js</code></li>
            <li>• Your Firebase credentials are correct</li>
            <li>• Firestore is enabled in Firebase Console</li>
            <li>• Security rules are set properly</li>
          </ul>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 font-semibold transition-all active:scale-95 shadow-[0_12px_28px_-8px_rgba(249,115,22,0.5)]"
          >
            <RefreshCcw className="w-4 h-4" strokeWidth={2} />
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      {/* Header — matches storefront navbar */}
      <header className="bg-black/90 backdrop-blur-2xl sticky top-0 z-50 shadow-[0_1px_0_rgba(255,255,255,0.08),0_10px_40px_-10px_rgba(0,0,0,0.6)]">
        <div className="container mx-auto px-4 md:px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Logo className="h-8 text-white flex-shrink-0" />
            <div className="hidden sm:block h-6 w-px bg-white/15" />
            <span className="hidden sm:block text-[11px] font-bold tracking-[0.2em] uppercase text-orange-500 whitespace-nowrap">
              Admin Panel
            </span>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden md:flex items-center gap-2 px-3.5 py-2 bg-green-500/10 border border-green-500/20 text-green-400 rounded-full text-xs font-semibold">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
              Firebase Connected
            </div>

            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white hover:bg-white/10 rounded-full text-sm font-medium transition-all"
            >
              <ExternalLink className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">View Store</span>
            </a>

            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-red-500 border border-white/10 hover:border-red-500 text-white rounded-full text-sm font-semibold transition-all duration-300 active:scale-95"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.75} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs — pill segmented control */}
      <div className="sticky top-[64px] z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200/60 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
        <div className="container mx-auto px-4 md:px-6 py-3">
          <nav className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 active:scale-95 ${
                  activeTab === id
                    ? 'bg-[#1d1d1f] text-white shadow-[0_8px_20px_-6px_rgba(0,0,0,0.4)]'
                    : 'bg-gray-100/80 text-gray-600 hover:bg-gray-200/80 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.75} />
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="container mx-auto px-4 md:px-6 py-8">
        {activeTab === 'products' && <ProductsTab />}
        {activeTab === 'categories' && <CategoriesTab />}
        {activeTab === 'settings' && <SettingsTab />}
      </main>
    </div>
  );
};

export default AdminPanel;
